/* ANL-07 / E16.1 — de onde veio cada pedido.
 *
 * Grava a origem da visita e a carimba no carrinho, para que o pedido no admin
 * diga de onde veio, sem app e sem GA4.
 *
 * ⚠️ O CARIMBO FICA FORA DO CAMINHO DA COMPRA, E ISSO FOI DECIDIDO NO DEBATE.
 * A redação original da tarefa mandava carimbar na sequência
 * `update.js` → `add.js` → `cart.js`. Mas `assets/cofcof-minicart.js:497-504`
 * já faz `add.js` → `cart.js` em série, com o botão travado enquanto isso:
 * enfiar um terceiro salto ali é atraso na única tela que cobra. O requisito
 * real é "o atributo existe quando o pedido fecha", não uma ordem de chamadas.
 * Então o carimbo acontece **uma vez por sessão, no carregamento**, e o
 * caminho de adicionar ao carrinho não é tocado.
 *
 * ⚠️ "PRIVADO" É MENOS DO QUE PARECE — MEDIDO, NÃO LIDO NA DOCUMENTAÇÃO.
 * O prefixo `__` esconde o atributo do `/cart.js` e do Liquid (conferido: em
 * carrinho vazio o `/cart.js` devolveu `Origem` e **não** devolveu
 * `__cof_attr`). Mas a Shopify continua serializando o atributo num blob JSON
 * dentro do HTML da página do carrinho. Não aparece para o olho
 * (`document.body.innerText` não o contém), e aparece no fonte. Conclusão
 * prática: **nada sensível entra aqui**. Só UTM, referrer e um id pseudônimo.
 *
 * ⚠️ SENTINELA: a origem é gravada SEMPRE, mesmo vazia (`(direto)`).
 * É o que separa "pedido que veio direto" de "pedido de quem estava sem
 * JavaScript" — sem isso, os dois viram "direto" no relatório, e o instrumento
 * mente a favor de quem não paga. `templates/cart.liquid` garante compra sem
 * JS, então esses pedidos existem de verdade.
 *
 * Decisões do Tiago em 13/09/2026: chave própria (`atribuicao_enabled`), porque
 * isto vale sem GA4 nenhum; e **carimba mesmo sob recusa de consentimento**,
 * por legítimo interesse — em troca, nada de identificável entra no atributo.
 */
(function (global) {
  'use strict';

  var CHAVE_FT = 'cof_ft';
  var CHAVE_LT = 'cof_lt';
  var CHAVE_CID = 'cof_cid';
  var CHAVE_CARIMBADO = 'cof_carimbado';
  var DIAS_FT = 90;
  var MS_DIA = 86400000;

  var UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CLICK_IDS = ['gclid', 'fbclid', 'ttclid', 'msclkid', 'igshid'];

  /* Hosts que NÃO são origem externa. O checkout é o caso que estraga tudo: sem
   * ele nesta lista, toda compra vira "referral/checkout.shopify.com" e o
   * relatório passa a dizer que a loja é o próprio maior canal de aquisição. */
  var CASA = /(^|\.)(cofcof\.co|myshopify\.com|shopify\.com|shopifycs\.com|shop\.app|pay\.shopify\.com)$/i;

  function ehDaCasa(host) { return !host || CASA.test(host); }

  function hostDe(url) {
    try { return new URL(url).host; } catch (e) { return ''; }
  }

  function lerJson(texto) {
    if (!texto) return null;
    try { return JSON.parse(texto); } catch (e) { return null; }
  }

  /* Só aceita o que a gente sabe nomear, e com tamanho limitado: UTM é entrada
   * do visitante e vai parar no admin do lojista. */
  function limparValor(v) {
    if (typeof v !== 'string') return '';
    return v.replace(/[<>"'`]/g, '').slice(0, 120);
  }

  /**
   * @param {object} dep
   * @param {function} dep.agora           relógio em ms
   * @param {object}   dep.local           armazenamento persistente (getItem/setItem)
   * @param {object}   dep.sessao          armazenamento de sessão
   * @param {string}   dep.url             URL corrente
   * @param {string}   dep.referrer        document.referrer
   * @param {function} dep.enviar          (attrs) => Promise — o POST no carrinho
   * @param {function} [dep.novoId]        gerador do id pseudônimo
   * @param {function} [dep.aoErro]
   */
  function criarAtribuicao(dep) {
    function pegar(armazem, chave) {
      try { return armazem.getItem(chave); } catch (e) { return null; }
    }
    function guardar(armazem, chave, valor) {
      /* Modo privativo do Safari e cookies bloqueados fazem `setItem` lançar.
       * Atribuição não pode derrubar a página por causa disso. */
      try { armazem.setItem(chave, valor); return true; } catch (e) { return false; }
    }

    function parametros() {
      var busca = {};
      var q = String(dep.url).split('?')[1];
      if (!q) return busca;
      q.split('#')[0].split('&').forEach(function (par) {
        var i = par.indexOf('=');
        if (i < 0) return;
        var k = decodeURIComponent(par.slice(0, i));
        var v = limparValor(decodeURIComponent(par.slice(i + 1).replace(/\+/g, ' ')));
        if (v) busca[k] = v;
      });
      return busca;
    }

    function toqueAtual() {
      var p = parametros();
      var toque = { ts: dep.agora() };
      var temAlgo = false;

      UTMS.forEach(function (k) { if (p[k]) { toque[k] = p[k]; temAlgo = true; } });
      CLICK_IDS.forEach(function (k) { if (p[k]) { toque[k] = p[k]; temAlgo = true; } });

      var host = hostDe(dep.referrer);
      if (!ehDaCasa(host)) { toque.referrer = host; temAlgo = true; }

      try { toque.landing = new URL(dep.url).pathname; } catch (e) { toque.landing = '/'; }
      return temAlgo ? toque : null;
    }

    return {
      /* First-touch: escrito UMA vez. A expiração é conferida na LEITURA —
       * gravar uma validade no futuro e nunca checá-la é o erro comum, e
       * produz um "primeiro contato" de dois anos atrás sem ninguém perceber. */
      primeiroToque: function () {
        var bruto = lerJson(pegar(dep.local, CHAVE_FT));
        if (bruto && bruto.ts && (dep.agora() - bruto.ts) <= DIAS_FT * MS_DIA) return bruto;
        return null;
      },

      registrar: function () {
        var novo = toqueAtual();

        var ft = this.primeiroToque();
        if (!ft) {
          /* Primeira visita (ou a anterior expirou): mesmo sem UTM, o primeiro
           * contato existe — foi direto. Gravar isso é o que permite dizer
           * depois "ele chegou direto e voltou por anúncio". */
          ft = novo || { ts: dep.agora(), landing: (function () {
            try { return new URL(dep.url).pathname; } catch (e) { return '/'; }
          })() };
          guardar(dep.local, CHAVE_FT, JSON.stringify(ft));
        }

        /* Last-touch só é sobrescrito quando há sinal novo de fora. Sem esta
         * condição, navegar dentro do site apagaria a campanha que trouxe a
         * pessoa, porque cada página nova viria sem UTM. */
        if (novo) guardar(dep.sessao, CHAVE_LT, JSON.stringify(novo));

        if (!pegar(dep.local, CHAVE_CID)) {
          guardar(dep.local, CHAVE_CID, (dep.novoId ? dep.novoId() : String(dep.agora())));
        }
        return { ft: ft, lt: lerJson(pegar(dep.sessao, CHAVE_LT)) };
      },

      /* Os atributos como o Tiago vê no pedido: rótulo em português, valor
       * curto. O blob cru vai num campo à parte. */
      atributos: function () {
        var e = this.registrar();
        var ft = e.ft || {};
        var lt = e.lt || {};
        var attrs = {
          'Origem': lt.utm_source || lt.referrer || '(direto)',
          'Mídia': lt.utm_medium || (lt.referrer ? 'referral' : '(nenhuma)'),
          'Campanha': lt.utm_campaign || '(nenhuma)',
          'Primeiro contato': ft.utm_source || ft.referrer || '(direto)',
          'Página de entrada': ft.landing || '/',
        };
        attrs.__cof_attr = JSON.stringify({
          ft: ft, lt: lt, cid: pegar(dep.local, CHAVE_CID) || null, v: 1,
        }).slice(0, 900);
        return attrs;
      },

      /* Carimba uma vez por sessão. Devolve o que fez, para o teste afirmar o
       * efeito e não a intenção. */
      carimbar: function () {
        if (pegar(dep.sessao, CHAVE_CARIMBADO) === '1') return { pulou: 'ja-carimbado' };
        var attrs = this.atributos();
        guardar(dep.sessao, CHAVE_CARIMBADO, '1');
        try {
          var r = dep.enviar(attrs);
          if (r && typeof r.catch === 'function') {
            /* Falha de rede aqui não é problema de ninguém além do relatório. */
            r.catch(function (err) { if (dep.aoErro) dep.aoErro(err); });
          }
        } catch (err) {
          if (dep.aoErro) dep.aoErro(err);
          return { enviado: false, attrs: attrs };
        }
        return { enviado: true, attrs: attrs };
      },
    };
  }

  var API = { criarAtribuicao: criarAtribuicao, CHAVES: {
    ft: CHAVE_FT, lt: CHAVE_LT, cid: CHAVE_CID, carimbado: CHAVE_CARIMBADO,
  }, DIAS_FT: DIAS_FT, ehDaCasa: ehDaCasa };

  global.CofCofAtribuicao = API;

  if (typeof document === 'undefined') return;

  function iniciar() {
    var ilha = document.getElementById('cofcof-analytics-contexto');
    var ctx = {};
    if (ilha) { try { ctx = JSON.parse(ilha.textContent || '{}'); } catch (e) { ctx = {}; } }
    if (!ctx.atribuicao) return;

    var a = criarAtribuicao({
      agora: function () { return Date.now(); },
      local: global.localStorage,
      sessao: global.sessionStorage,
      url: global.location.href,
      referrer: document.referrer,
      novoId: function () {
        if (global.crypto && global.crypto.randomUUID) return global.crypto.randomUUID();
        return 'c' + Date.now().toString(36) + Math.floor(Math.random() * 1e9).toString(36);
      },
      enviar: function (attrs) {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
        });
      },
    });

    global.CofCofAtribuicao.instancia = a;
    a.carimbar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})(typeof window !== 'undefined' ? window : globalThis);
